import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Dashboard from './pages/Dashboard';
import CustomerList from './pages/Customers/CustomerList';
import CustomerDetails from './pages/Customers/CustomerDetails';
import PensionFundList from './pages/PensionFunds/PensionFundList';
import PensionFundDetails from './pages/PensionFunds/PensionFundDetails';
import WorkflowPage from './pages/Workflow/WorkflowPage';
import WorkflowCustomerDetails from './pages/Workflow/WorkflowCustomerDetails';
import InterventionList from './pages/Interventions/InterventionList';
import UserList from './pages/Users/UserList';
import CaisseList from './pages/Caisses/CaisseList';
import PdfMerger from './pages/Tools/PdfMerger';
import ExportPage from './pages/Exports/ExportPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { token, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const userRole = user?.advisor?.role?.name?.toLowerCase() || '';
  const isAdmin = userRole === 'admin';

  if (allowedRoles && !isAdmin && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Layout>
                        <CustomerList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:uuid"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Layout>
                        <CustomerDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pension-funds"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'finance']}>
                      <Layout>
                        <PensionFundList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pension-funds/:uuid"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'finance']}>
                      <Layout>
                        <PensionFundDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workflow"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'workflow']}>
                      <Layout>
                        <WorkflowPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workflow/customers/:uuid"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'workflow']}>
                      <Layout>
                        <WorkflowCustomerDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/interventions"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'workflow']}>
                      <Layout>
                        <InterventionList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Layout>
                        <UserList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/caisses"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Layout>
                        <CaisseList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exports"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ExportPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tools/pdf-merger"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <PdfMerger />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
